// src/components/classTabs/StudentsTab.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from '../../api/axiosInstance';
import {
  Box, Typography, Stack, Button, Paper, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Checkbox, List, ListItem, ListItemText,
  ListItemSecondaryAction, Alert, TextField, InputAdornment, CircularProgress
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import GroupIcon from '@mui/icons-material/Group';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DynamicDataGrid from '../DynamicDataGrid';
import { bulkTransferStudents } from '../../slices/studentsSlice';
import { fetchClasses } from '../../slices/classesSlice';
import { fetchClassById } from '../../slices/classesSlice';
import { useToast } from '../../context/ToastContext';

const StudentsTab = ({ activeStudents, selectedClass, handleExportExcelModern, handlePrintModern }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userRole = localStorage.getItem('role');
  const isTeacher = userRole === 'teacher';
  const { showToast } = useToast();
  const { list: classesList } = useSelector(state => state.classes || {});
  const [selectedStudents, setSelectedStudents] = useState([]);
  
  // Ensure selectedStudents is always an array
  const safeSelectedStudents = Array.isArray(selectedStudents) ? selectedStudents : [];
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [targetClassId, setTargetClassId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStudents, setFilteredStudents] = useState(activeStudents);

  // Add students from another class
  const [addStudentsDialogOpen, setAddStudentsDialogOpen] = useState(false);
  const [sourceClassId, setSourceClassId] = useState('');
  const [sourceClassStudents, setSourceClassStudents] = useState([]);
  const [selectedSourceStudents, setSelectedSourceStudents] = useState([]);
  const [loadingSourceStudents, setLoadingSourceStudents] = useState(false);

  useEffect(() => {
    dispatch(fetchClasses());
  }, [dispatch]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = activeStudents.filter(student => {
        const fullName = `${student.first_name || ''} ${student.second_name || ''}`.toLowerCase();
        const studentId = (student.student_id || '').toLowerCase();
        const query = searchQuery.toLowerCase();
        return fullName.includes(query) || studentId.includes(query);
      });
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(activeStudents);
    }
  }, [searchQuery, activeStudents]);

  const handleViewStudent = (studentId) => {
    navigate(`/students/view/${studentId}`);
  };

  const handleSelectionChange = React.useCallback((newSelection) => {
    // Handle different selection formats
    let selection = [];
    if (Array.isArray(newSelection)) {
      selection = newSelection;
    } else if (newSelection && typeof newSelection === 'object') {
      // If it's an object (like a Set or Map), convert to array
      selection = Array.from(newSelection);
    } else if (newSelection) {
      selection = [newSelection];
    }
    setSelectedStudents(selection);
  }, []);
  
  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const handleOpenTransferDialog = () => {
    if (safeSelectedStudents.length === 0) {
      showToast('يرجى تحديد طلاب للنقل', 'warning');
      return;
    }
    setTransferDialogOpen(true);
  };

  const handleCloseTransferDialog = () => {
    setTransferDialogOpen(false);
    setTargetClassId('');
  };

  const handleConfirmTransfer = async () => {
    if (!targetClassId) {
      showToast('يرجى اختيار الصف الهدف', 'warning');
      return;
    }

    try {
      const result = await dispatch(bulkTransferStudents({
        studentIds: safeSelectedStudents,
        targetClassId: targetClassId
      })).unwrap();

      if (result.success) {
        showToast(`✅ ${result.message}`, 'success');
        setSelectedStudents([]);
        setTransferDialogOpen(false);
        setTargetClassId('');
        setSearchQuery('');
        // Refresh class data
        if (selectedClass?.id) {
          dispatch(fetchClassById(selectedClass.id));
        }
      } else {
        showToast('❌ حدث خطأ أثناء نقل الطلاب', 'error');
      }
    } catch (error) {
      showToast(error?.error || '❌ فشل في نقل الطلاب', 'error');
    }
  };

  const selectedStudentsData = filteredStudents.filter(s => safeSelectedStudents.includes(s.id));

  const handleOpenAddStudentsDialog = () => {
    setSourceClassId('');
    setSourceClassStudents([]);
    setSelectedSourceStudents([]);
    setAddStudentsDialogOpen(true);
  };

  const handleCloseAddStudentsDialog = () => {
    setAddStudentsDialogOpen(false);
    setSourceClassId('');
    setSourceClassStudents([]);
    setSelectedSourceStudents([]);
  };

  const handleSourceClassChange = async (classId) => {
    setSourceClassId(classId);
    setSelectedSourceStudents([]);
    if (!classId) {
      setSourceClassStudents([]);
      return;
    }
    setLoadingSourceStudents(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/?school_class=${classId}&is_archived=false`
      );
      setSourceClassStudents(response.data);
    } catch {
      showToast('❌ فشل في تحميل طلاب الصف المحدد', 'error');
      setSourceClassStudents([]);
    } finally {
      setLoadingSourceStudents(false);
    }
  };

  const handleToggleSourceStudent = (studentId) => {
    setSelectedSourceStudents(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSelectAllSourceStudents = () => {
    if (selectedSourceStudents.length === sourceClassStudents.length) {
      setSelectedSourceStudents([]);
    } else {
      setSelectedSourceStudents(sourceClassStudents.map(s => s.id));
    }
  };

  const handleConfirmAddStudents = async () => {
    if (selectedSourceStudents.length === 0) {
      showToast('يرجى تحديد طلاب للإضافة', 'warning');
      return;
    }
    try {
      const result = await dispatch(bulkTransferStudents({
        studentIds: selectedSourceStudents,
        targetClassId: selectedClass?.id
      })).unwrap();

      if (result.success) {
        showToast(`✅ ${result.message}`, 'success');
        handleCloseAddStudentsDialog();
        if (selectedClass?.id) {
          dispatch(fetchClassById(selectedClass.id));
        }
      } else {
        showToast('❌ حدث خطأ أثناء إضافة الطلاب', 'error');
      }
    } catch (error) {
      showToast(error?.error || '❌ فشل في إضافة الطلاب', 'error');
    }
  };

  const columns = [
    { field: 'first_name', headerName: 'الاسم الأول', flex: 1 },
    { field: 'second_name', headerName: 'اسم العائلة', flex: 1 },
    { field: 'parent_name', headerName: 'اسم ولي الأمر', flex: 1 },
    { field: 'parent_phone', headerName: 'الهاتف', flex: 1 },
    { 
      field: 'is_bus_joined', 
      headerName: 'النقل', 
      flex: 0.7,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'نعم' : 'لا'} 
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    { field: 'date_of_registration', headerName: 'تاريخ التسجيل', flex: 1 },
    // {
    //   field: 'actions',
    //   headerName: 'الإجراءات',
    //   flex: 0.8,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Stack useFlexGap direction="row" spacing={1} alignItems="center">
    //       <IconButton
    //         size="small"
    //         color="primary"
    //         onClick={() => handleViewStudent(params.row.id)}
    //         sx={{
    //           backgroundColor: 'primary.50',
    //           '&:hover': {
    //             backgroundColor: 'primary.100',
    //           }
    //         }}
    //       >
    //         <VisibilityIcon fontSize="small" />
    //       </IconButton>
    //       <Button
    //         size="small"
    //         variant="outlined"
    //         startIcon={<PersonIcon />}
    //         onClick={() => handleViewStudent(params.row.id)}
    //         sx={{
    //           borderRadius: 2,
    //           fontSize: '0.75rem',
    //           px: 1.5,
    //           py: 0.5
    //         }}
    //       >
    //         الملف الأكاديمي
    //       </Button>
    //     </Stack>
    //   )
    // }
  ];

  return (
    <>
      {/* Export Actions */}
      <Paper
        elevation={1}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          background: '#fff'
        }}
      >
        <Stack useFlexGap direction="row" spacing={2} sx={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcelModern}
            sx={{
              borderRadius: 2,
              fontWeight: 'bold',
              px: 2.5,
              minWidth: 130
            }}
          >
            تصدير Excel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePrintModern}
            sx={{
              borderRadius: 2,
              fontWeight: 'bold',
              px: 2.5,
              minWidth: 130
            }}
          >
            طباعة
          </Button>
          {!isTeacher && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SwapHorizIcon />}
              onClick={handleOpenTransferDialog}
              disabled={safeSelectedStudents.length === 0}
              sx={{
                borderRadius: 2,
                fontWeight: 'bold',
                px: 2.5,
                minWidth: 150
              }}
            >
              نقل {safeSelectedStudents.length > 0 ? `(${safeSelectedStudents.length})` : ''}
            </Button>
          )}
          {!isTeacher && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenAddStudentsDialog}
              sx={{
                borderRadius: 2,
                fontWeight: 'bold',
                px: 2.5,
                minWidth: 160
              }}
            >
              إضافة طلاب من صف
            </Button>
          )}
        </Stack>
        
        {safeSelectedStudents.length > 0 && (
          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
            تم تحديد {safeSelectedStudents.length} طالب
          </Typography>
        )}

        {/* <Typography variant="body2" color="text.secondary">
          انقر على "الملف الأكاديمي" لعرض تفاصيل الطالب الأكاديمية
        </Typography> */}
      </Paper>

      {/* Students Data Grid */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          p: { xs: 2, sm: 4 },
          background: '#fff',
          boxShadow: '0 2px 12px 0 rgba(44,62,80,0.07)'
        }}
      >
        {activeStudents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              لا يوجد طلاب نشطين في هذا الصف
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              جميع الطلاب في هذا الصف مؤرشفين أو لم يتم إضافة طلاب بعد
            </Typography>
          </Box>
        ) : (
          <>
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="ابحث عن طالب..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            <DynamicDataGrid
              columns={columns}
              rows={filteredStudents.map((s) => {
                // Ensure id is a string and ensure it exists
                const rowId = String(s.id || s.uuid || s._id || `student-${s.student_id || Math.random()}`);
                return { id: rowId, ...s };
              })}
              paginationProps={{
                labelRowsPerPage: 'عدد الصفوف في الصفحة:',
                labelDisplayedRows: ({ from, to, count }) =>
                  `عرض ${from}–${to} من ${count !== -1 ? count : `أكثر من ${to}`} طالب نشط`,
                dir: 'rtl'
              }}
              checkboxSelection={true}
              onSelectionModelChange={handleSelectionChange}
              onRowClick={(params, event) => {
                if (event.target.closest('.MuiCheckbox-root')) return;
                handleViewStudent(params.row.id);
              }}
            />
          </>
        )}
      </Paper>

      {/* Add Students from Another Class Dialog */}
      <Dialog
        open={addStudentsDialogOpen}
        onClose={handleCloseAddStudentsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
            <PersonAddIcon color="primary" />
            <Typography variant="h6">إضافة طلاب إلى صف "{selectedClass?.name}"</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              اختر الصف المصدر لعرض طلابه، ثم حدد الطلاب الذين تريد نقلهم إلى هذا الصف
            </Alert>

            <FormControl fullWidth>
              <InputLabel>اختر الصف المصدر</InputLabel>
              <Select
                value={sourceClassId}
                onChange={(e) => handleSourceClassChange(e.target.value)}
                label="اختر الصف المصدر"
              >
                {classesList
                  ?.filter(cls => cls.id !== selectedClass?.id)
                  ?.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {loadingSourceStudents && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loadingSourceStudents && sourceClassId && sourceClassStudents.length === 0 && (
              <Alert severity="warning">لا يوجد طلاب نشطين في هذا الصف</Alert>
            )}

            {!loadingSourceStudents && sourceClassStudents.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    طلاب الصف ({sourceClassStudents.length}):
                  </Typography>
                  <Button size="small" onClick={handleSelectAllSourceStudents}>
                    {selectedSourceStudents.length === sourceClassStudents.length ? 'إلغاء الكل' : 'تحديد الكل'}
                  </Button>
                </Stack>
                <List dense sx={{ maxHeight: 250, overflow: 'auto', bgcolor: 'background.paper', border: '1px solid #E5E7EB', borderRadius: 1 }}>
                  {sourceClassStudents.map((student) => (
                    <ListItem
                      key={student.id}
                      button
                      onClick={() => handleToggleSourceStudent(student.id)}
                      selected={selectedSourceStudents.includes(student.id)}
                    >
                      <Checkbox
                        edge="start"
                        checked={selectedSourceStudents.includes(student.id)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                      <ListItemText
                        primary={`${student.first_name} ${student.second_name}`}
                        secondary={student.student_id ? `رقم: ${student.student_id}` : ''}
                      />
                    </ListItem>
                  ))}
                </List>
                {selectedSourceStudents.length > 0 && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    تم تحديد {selectedSourceStudents.length} طالب
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddStudentsDialog} color="inherit">
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmAddStudents}
            variant="contained"
            color="primary"
            disabled={selectedSourceStudents.length === 0}
          >
            إضافة إلى الصف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog
        open={transferDialogOpen}
        onClose={handleCloseTransferDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
            <SwapHorizIcon color="primary" />
            <Typography variant="h6">نقل الطلاب إلى صف آخر</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              سيتم نقل {safeSelectedStudents.length} طالب من الصف "{selectedClass?.name}" إلى الصف المحدد
            </Alert>

            <FormControl fullWidth>
              <InputLabel>اختر الصف الهدف</InputLabel>
              <Select
                value={targetClassId}
                onChange={(e) => setTargetClassId(e.target.value)}
                label="اختر الصف الهدف"
              >
                {classesList
                  ?.filter(cls => cls.id !== selectedClass?.id)
                  ?.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                الطلاب الذين سيتم نقلهم ({safeSelectedStudents.length}):
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper' }}>
                {selectedStudentsData.map((student) => (
                  <ListItem key={student.id}>
                    <ListItemText
                      primary={`${student.first_name} ${student.second_name}`}
                      secondary={student.student_id ? `رقم الطالب: ${student.student_id}` : ''}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseTransferDialog} color="inherit">
            إلغاء
          </Button>
          <Button 
            onClick={handleConfirmTransfer} 
            variant="contained" 
            color="primary"
            disabled={!targetClassId}
          >
            تأكيد النقل
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StudentsTab;